import { zodResolver } from '@hookform/resolvers/zod';
import {
  ContactRequestSchema,
  ContactStatusEnum,
  IContactRequestSchema,
  IContactRespSchema,
} from '@monetize/types/app';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdClear, MdStar, MdStarOutline } from 'react-icons/md';
import { handleApiErrorToast } from '../../../../../api/axios';
import {
  useChangeDefaultEsigner,
  useGetEsignContacts,
} from '../../../../../api/contactsService';
import {
  useCreateEntity,
  usePerformEntityAction,
} from '../../../../../api/queryUtils';
import {
  MBox,
  MButton,
  MCustomIconButton,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MInput,
  MStack,
  MText,
  MTooltip,
} from '../../../../../components/Monetize';
import {
  getHighlightStyles,
  useSettingsSectionHighlight,
} from '../../../../../hooks/useSettingsSectionHighlight';
import { useACL } from '../../../../../services/acl/acl';
import { useConfirmModal } from '../../../../../services/confirmModal';
import { logger } from '../../../../../services/logger';
import { SettingsSection } from '../../../components/SettingsSection';
import { QUOTE_SETTING_KEYS } from '../quoteSettingsConfig';

const DEFAULT_CONTACT = {
  status: ContactStatusEnum.ACTIVE,
  internal: true,
  esigner: true,
  customFields: {},
} as const;

export const QuoteEsignSettingsDefaultSigningContacts = () => {
  const isContactsHighlighted = useSettingsSectionHighlight(
    QUOTE_SETTING_KEYS.quoteEsignDefaultContacts,
  );
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [internalDefaultContactId, setInternalDefaultContactId] = useState<
    string | undefined
  >();
  const [removingContactId, setRemovingContactId] = useState<
    string | undefined
  >();
  const { showConfirmModal, setModalLoading, closeModal } = useConfirmModal();
  const { canDo } = useACL();

  const formProps = useForm<IContactRequestSchema>({
    resolver: zodResolver(ContactRequestSchema),
    mode: 'onSubmit',
    defaultValues: DEFAULT_CONTACT,
  });
  const { reset } = formProps;

  const { allContacts, defaultContact } = useGetEsignContacts();

  const defaultContactId = defaultContact?.id;

  const { mutateAsync: createESignContactMutate, isPending: createLoading } =
    useCreateEntity<IContactRespSchema, IContactRequestSchema>('contacts', {
      onSuccess: () => {
        reset(DEFAULT_CONTACT);
        setIsAddingNew(false);
      },
      onError: (err: any) => handleApiErrorToast(err),
    });

  const { mutateAsync: doChangeDefaultEsigner, isPending: configureLoading } =
    useChangeDefaultEsigner(defaultContactId, {
      onError: (err: any) => handleApiErrorToast(err),
    });

  const { mutateAsync: doContactAction, isPending: removeLoading } =
    usePerformEntityAction<IContactRespSchema>('contacts', {
      onError: (err: any) => handleApiErrorToast(err),
    });

  const onContactSubmit = (payload: IContactRequestSchema) => {
    createESignContactMutate(payload);
  };

  const handleDeleteESignContact = (contactId: string, fullName: string) => {
    const onYes = async () => {
      try {
        setModalLoading(true);
        setRemovingContactId(contactId);
        await doContactAction({ id: contactId, action: 'archive' });
        closeModal();
      } catch (err) {
        handleApiErrorToast(err);
      } finally {
        setModalLoading(false);
        setRemovingContactId(undefined);
      }
    };

    showConfirmModal({
      title: `Are you sure you want to delete "${fullName}"?`,
      description:
        'Once removed, the signing contact will no longer be available.',
      onYes,
      yesBtnProps: {
        variant: 'delete' as any,
      },
      noBtnProps: {
        variant: 'cancel' as any,
      },
    });
  };

  const handleChangeDefaultEsigner = (contactId: string) => {
    if (contactId === defaultContactId) {
      return;
    }
    setInternalDefaultContactId(contactId);
    doChangeDefaultEsigner({ id: contactId });
  };

  return (
    <SettingsSection
      label="Default Signing Contacts"
      id={QUOTE_SETTING_KEYS.quoteEsignDefaultContacts}
      {...getHighlightStyles(isContactsHighlighted)}
      mb={2}
    >
      <MText color="tPurple.dark" mb={4}>
        Make these contacts available to be used when eSigning Quotes.
      </MText>
      {allContacts.map((contact) => (
        <ContactRow
          key={contact.id}
          contact={contact}
          isLoading={
            configureLoading && contact.id === internalDefaultContactId
          }
          isRemoveLoading={removeLoading && contact.id === removingContactId}
          onDelete={handleDeleteESignContact}
          onSetDefault={handleChangeDefaultEsigner}
          isDisabled={configureLoading || removeLoading}
        />
      ))}
      <MBox mt={4} />

      {(!allContacts?.length || isAddingNew) && (
        <AddContactForm
          formProps={formProps}
          onSubmit={onContactSubmit}
          onCancel={() => {
            setIsAddingNew(false);
            reset(DEFAULT_CONTACT);
          }}
          isLoading={createLoading}
        />
      )}

      {allContacts?.length &&
      !isAddingNew &&
      canDo([['account_contacts', 'create']], true) ? (
        <MButton
          variant="tertiary"
          fontSize="sm"
          onClick={() => setIsAddingNew(true)}
          p={0}
          m={0}
          mb={3.5}
        >
          + Add Contact
        </MButton>
      ) : null}
    </SettingsSection>
  );
};

interface ContactRowProps {
  contact: IContactRespSchema;
  isLoading?: boolean;
  isRemoveLoading?: boolean;
  isDisabled?: boolean;
  onDelete: (contactId: string, fullName: string) => void;
  onSetDefault: (contactId: string) => void;
}

const ContactRow = ({
  contact,
  isLoading,
  isRemoveLoading,
  isDisabled,
  onDelete,
  onSetDefault,
}: ContactRowProps) => {
  const { id: contactId, fullName, email, defaultESigner } = contact;

  return (
    <MFlex
      justifyContent="space-between"
      alignItems="center"
      borderBottom="1px"
      borderBottomColor="tGray.back"
      py={3}
      h={57}
    >
      <MBox>
        <MText py={0} m={0} fontSize="md">
          {fullName}
        </MText>
        <MText py={0} m={0} fontSize="sm" color="tGray.darkPurple">
          {email}
        </MText>
      </MBox>
      <MStack gap={4} direction="row" alignItems="center">
        <MTooltip
          label={
            defaultESigner
              ? 'Default signing contact'
              : 'Set as default signing contact'
          }
          placement="bottom-end"
        >
          <MBox>
            <MCustomIconButton
              isDisabled={isDisabled}
              isLoading={isLoading}
              variant="icon"
              btnSize={5}
              padding={0}
              containerSize={8}
              icon={defaultESigner ? MdStar : MdStarOutline}
              aria-label="Set as default signing contact"
              onClick={() => onSetDefault(contactId)}
            />
          </MBox>
        </MTooltip>
        <MTooltip
          label={defaultESigner ? 'Cannot remove default' : 'Remove'}
          placement="bottom-end"
        >
          <MBox>
            <MCustomIconButton
              isLoading={isRemoveLoading}
              isDisabled={isDisabled || defaultESigner}
              variant="icon"
              btnSize={3}
              padding={0}
              containerSize={6}
              iconProps={{
                _active: { color: 'unset' },
                _focus: { color: 'unset' },
              }}
              icon={MdClear}
              aria-label="Remove"
              onClick={() => onDelete(contactId, fullName)}
            />
          </MBox>
        </MTooltip>
      </MStack>
    </MFlex>
  );
};

interface AddContactFormProps {
  formProps: ReturnType<typeof useForm<IContactRequestSchema>>;
  isLoading: boolean;
  onSubmit: (data: IContactRequestSchema) => void;
  onCancel: () => void;
}

const AddContactForm = ({
  formProps,
  onSubmit,
  onCancel,
  isLoading,
}: AddContactFormProps) => {
  const onError = (err: any) => {
    logger.warn(err);
  };

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty },
  } = formProps;

  return (
    <form autoComplete="off" onSubmit={handleSubmit(onSubmit, onError)}>
      <MGrid
        templateColumns=".9fr .7fr 1fr .3fr .1fr"
        columnGap={4}
        mb={4}
        alignItems="end"
      >
        <MGridItem>
          <MFormField error={errors.fullName} label="Name" isRequired>
            <Controller
              name="fullName"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <MInput
                  autoFocus
                  autoComplete="off"
                  data-1p-ignore="true"
                  maxLength={40}
                  {...field}
                  isDisabled={isLoading}
                />
              )}
            />
          </MFormField>
        </MGridItem>

        <MGridItem>
          <MFormField error={errors.title} label="Job Title">
            <Controller
              name="title"
              control={control}
              defaultValue=""
              render={({ field: { value, ...rest } }) => (
                <MInput
                  maxLength={40}
                  value={value || ''}
                  {...rest}
                  isDisabled={isLoading}
                />
              )}
            />
          </MFormField>
        </MGridItem>

        <MGridItem>
          <MFormField error={errors.email} label="Email" isRequired>
            <Controller
              name="email"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <MInput
                  autoComplete="off"
                  data-1p-ignore="true"
                  {...field}
                  isDisabled={isLoading}
                />
              )}
            />
          </MFormField>
        </MGridItem>

        <MGridItem>
          <MButton
            w="77px"
            size="sm"
            type="submit"
            variant="primary"
            mt={7}
            alignSelf="self-end"
            isDisabled={!isDirty || isLoading}
            isLoading={isLoading}
          >
            Add
          </MButton>
        </MGridItem>

        <MGridItem>
          <MTooltip label="Close">
            <MBox>
              <MCustomIconButton
                variant="icon"
                btnSize={3.5}
                padding={0}
                containerSize={7}
                icon={MdClear}
                aria-label="Close"
                mt="30"
                onClick={() => {
                  onCancel();
                  reset(DEFAULT_CONTACT);
                }}
              />
            </MBox>
          </MTooltip>
        </MGridItem>
      </MGrid>
    </form>
  );
};
